import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import SignoutButton from '../Pages/Auth/Signout';
import logoicon from '../assets/logos/sustaincred-white-logo.svg'
import { 
  LayoutDashboard, 
  Footprints, 
  TrendingUp, 
  Wallet, 
  FileText, 
  BarChart3, 
  Bell, 
  Puzzle, 
  Settings,
  Menu,
  X,
  Leaf
} from 'lucide-react';
import { useTheme } from '../Theme/ThemeContext';
import ThemeToggle from '../Theme/ThemeToggle';

const navItems = [
  { name: 'Overview', icon: LayoutDashboard, path: '/Overview' },
  { name: 'Footprint Tracker', icon: Footprints, path: '/footprint-tracker' },
  { name: 'Investments Hub', icon: TrendingUp, path: '/investment-hub' },
  { name: 'Portfolio / Projects', icon: Wallet, path: '/portfolio' },
  { name: 'Reports & Compliance', icon: FileText, path: '/reports' },
  { name: 'SCM Analytics', icon: BarChart3, path: '/ai-insights' },
  { name: 'SCM integrations', icon: Puzzle, path: '/supply-chain-flow' },
  { name: 'Settings', icon: Settings, path: '/settings' },
];

const ProtectedLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();

  return (
    <div 
      className="flex h-screen overflow-hidden"
      style={{ 
        backgroundColor: theme.background,
        color: theme.text 
      }}
    >
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 flex flex-col`}
        style={{ 
          backgroundColor: theme.surface,
          borderRight: `1px solid ${theme.border}`
        }}
      >
        {/* Logo Section */}
        <div className="px-4 py-5 border-b" style={{ borderColor: theme.border }}>
          <div className="flex items-center gap-2.5">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            >
              <img src={logoicon}/>
            </div>
            {sidebarOpen && (
              <div>
                <span 
                  className="text-2xl font-bold tracking-tight block bg-gradient-to-r bg-clip-text text-transparent"
                  style={{ 
                    backgroundImage: `linear-gradient(135deg, ${theme.accent1}, ${theme.accent2})`,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text'
                  }}
                >
                  SCMart
                </span>
                <p className="text-[11px] font-medium tracking-wide mt-0.5" style={{ color: theme.textSecondary }}>
                  Carbon Accounting & Trading
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                    ${!sidebarOpen ? 'justify-center' : ''}
                    ${isActive 
                      ? 'bg-opacity-10 text-accent' 
                      : 'text-secondary hover:bg-opacity-20 hover:text-primary'
                    }
                  `}
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? `${theme.accent1}15` : 'transparent',
                    color: isActive ? theme.accent1 : theme.textSecondary,
                  })}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {sidebarOpen && <span>{item.name}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t" style={{ borderColor: theme.border }}>
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-secondary hover:text-accent hover:bg-opacity-20"
                style={{ 
                  color: theme.textSecondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${theme.hover}20`;
                  e.currentTarget.style.color = theme.accent1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme.textSecondary;
                }}
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </button>
              
              <SignoutButton 
                variant="icon-only" 
                onSignout={() => {}} 
                redirectTo="/signin" 
                showText={true}
                iconSize={4}
              />
            </div>
          ) : (
            <div className="flex justify-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center justify-center p-2 rounded-lg transition-all duration-200 w-full text-secondary hover:text-accent hover:bg-opacity-20"
                style={{ 
                  color: theme.textSecondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${theme.hover}20`;
                  e.currentTarget.style.color = theme.accent1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme.textSecondary;
                }}
              >
                <Menu className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
        <Outlet />
      </main>
    </div>
  );
};

export default ProtectedLayout;